import { first, inRange, last } from 'lodash';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import useStores from 'hooks/use-stores';
import HeightAnimationEffect from 'main/components/HeightAnimationEffect';
import GenerationLauncher from 'pages/Bulletin/components/AiInsights/components/GenerationLauncher';
import GenerationResult from 'pages/Bulletin/components/AiInsights/components/GenerationResult';
import { CaptchaRequiredError, useAiInsights } from 'queries/bulletins/use-ai-insights';
import gtagEvent from 'utils/gtag-event';

import GenerationProgress from './components/GenerationProgress';
import OopsMessage from './components/OopsMessage';
import { GENERATION_STEPS, GENERATION_STEPS_PROGRESS_MAP, GenerationStep } from './constants';
import useStyles from './styles';

interface Props {
    id: string;
    className?: string;
}

const REQUEST_DURATION = 60_000;
const POLL_INTERVAL = 5_000;
const UPDATE_INTERVAL = 500;
const FINISHING_DELAY = 1_000;
const MIN_DURATION = 5_000;

const AiInsights: React.FC<Props> = ({ id, className }) => {
    const { classes, cx } = useStyles();
    const { antiBotStore, userStore } = useStores();

    const {
        isError,
        error,
        data: insight,
        refetch
    } = useAiInsights(
        { id, timeout: REQUEST_DURATION, interval: POLL_INTERVAL },
        { enabled: false, select: ({ insight }) => insight }
    );

    const mutable = useRef({ startedAt: NaN, updateTimer: NaN, delayTimer: NaN });
    const [step, setStep] = useState<GenerationStep>();

    const cleanup = useCallback(() => {
        clearInterval(mutable.current.updateTimer);
        clearTimeout(mutable.current.delayTimer);
        mutable.current.startedAt = NaN;
        mutable.current.updateTimer = NaN;
        mutable.current.delayTimer = NaN;
    }, []);

    const startGenerationImitation = useCallback(() => {
        cleanup();
        mutable.current.startedAt = Date.now();

        const onTick = () => {
            const elapsed = (Date.now() - mutable.current.startedAt) / REQUEST_DURATION;
            setStep((current) =>
                current
                    ? GENERATION_STEPS.find((step) => inRange(elapsed, ...GENERATION_STEPS_PROGRESS_MAP[step])) ||
                      current
                    : current
            );
        };

        setStep(first(GENERATION_STEPS));
        mutable.current.updateTimer = window.setInterval(() => onTick(), UPDATE_INTERVAL);
    }, [cleanup]);

    const stopGenerationImitation = useCallback(
        (immediate?: boolean) => {
            const duration = Date.now() - mutable.current.startedAt;
            cleanup();

            if (immediate) {
                setStep(undefined);
            } else {
                setStep(last(GENERATION_STEPS));
                const delay = Math.max(MIN_DURATION - duration, FINISHING_DELAY);
                mutable.current.delayTimer = window.setTimeout(() => setStep(undefined), delay);
            }
        },
        [cleanup]
    );

    useEffect(() => {
        antiBotStore.randomCollectData();
        return () => cleanup();
    }, [antiBotStore, cleanup]);

    const generate = useCallback(
        (is_retry = false) => {
            gtagEvent(is_retry ? 'ai-insight-generation-retry' : 'ai-insight-generation', {
                is_authorized: userStore.isAuthorized,
                has_paid_license: userStore.hasLicense,
                has_only_free_license: userStore.hasOnlyFreeLicense
            });

            startGenerationImitation();
            void refetch()
                .then(() => stopGenerationImitation(false))
                .catch(() => stopGenerationImitation(true));
        },
        [
            refetch,
            startGenerationImitation,
            stopGenerationImitation,
            userStore.hasLicense,
            userStore.hasOnlyFreeLicense,
            userStore.isAuthorized
        ]
    );

    return (
        <div className={cx(classes.container, className)}>
            <HeightAnimationEffect duration={500}>
                {step ? (
                    <GenerationProgress step={step} className={classes.item} />
                ) : isError ? (
                    error instanceof CaptchaRequiredError ? null : (
                        <>
                            <OopsMessage onRetry={() => generate(true)} className={classes.item} />
                            <span className={classes.hatch} />
                        </>
                    )
                ) : insight ? (
                    <GenerationResult text={insight} className={classes.item} />
                ) : (
                    <>
                        <GenerationLauncher onLaunch={() => generate(false)} className={classes.item} />
                        <span className={classes.hatch} />
                    </>
                )}
            </HeightAnimationEffect>
        </div>
    );
};

export default observer(AiInsights);
